import { useEffect } from 'react';

/**
 * Radix non-modal popovers (>= react-popover 1.1.15) defer outside-click
 * dismissal to the `click` event and skip it when another library stops that
 * click's propagation before it bubbles back to the document — Headless UI's
 * Dialog does exactly that, so popovers opened inside a KoobModal never close
 * on outside click. Dismiss on the capture-phase `pointerdown` instead, which
 * runs before anything can intercept it.
 */
export default function useDismissOnOutsidePointerDown(
  open: boolean,
  onDismiss: () => void,
  ownedRefs: React.RefObject<HTMLElement | null>[],
) {
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      const inside =
        target && ownedRefs.some((ref) => ref.current?.contains(target));
      if (!inside) onDismiss();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () =>
      document.removeEventListener('pointerdown', onPointerDown, true);
    // ownedRefs are stable useRef objects; onDismiss is only read on event
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
}
